// Patient-facing consent screen before a per-minute telehealth authorisation (contract §9
// pins this file's path, default export and the `{quote, onAccept}` props exactly).
// States rate / floor / cap / the ceiling IN MONEY ("up to $240.00") and the flat no-show
// fee, and gates `onAccept` on an explicit consent checkbox — this row, timestamped
// server-side as `consult_consent` at intent creation, IS the dispute-pack evidence
// (gateway-flow-c-design.md §5.1/p.16): a checkbox that leaves no record is not consent.
import React, { useState } from "react";
import { useT } from "@/lib/LanguageContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Clock3 } from "lucide-react";
// Relative: gateway/ has no "@/" Jest shim yet — resolved for real, same convention as
// every gateway-consuming component in this build.
import { fmtMoney } from "../admin/money";

/**
 * @param {import("../gateway/contract").ConsultQuote} quote
 * @param {function} onAccept called once the checkbox is checked and the button clicked
 */
export default function ConsultCeilingDisclosure({ quote, onAccept }) {
  const t = useT("public");
  const [accepted, setAccepted] = useState(false);

  if (!quote) {
    return (
      <div className="text-sm text-eh-muted" data-testid="consult-disclosure-empty">
        {t("consult.disclosure.noQuote")}
      </div>
    );
  }

  const currency = quote.currency;

  return (
    <div className="bg-white rounded-2xl border border-eh-border-soft p-6 space-y-4" data-testid="consult-ceiling-disclosure">
      <div className="flex items-center gap-2">
        <Clock3 className="w-5 h-5 text-sol-gold" />
        <h3 className="font-display text-xl">{t("consult.disclosure.title")}</h3>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 text-sm">
        <div className="rounded-xl bg-eh-cream px-4 py-3" data-testid="consult-disclosure-rate">
          <div className="text-xs text-eh-muted">{t("consult.disclosure.rateLabel")}</div>
          <div className="font-semibold mt-0.5">{t("consult.disclosure.ratePerMinute", { amount: fmtMoney(quote.ratePerMinuteCents, currency) })}</div>
        </div>
        {/* Additive, charged once, never absorbed (contract §3, AMENDED 2026-08-27) —
            one of the five figures a chargeback turns on, so it is stated here even when
            it is 0: every number in this screen comes from the quote, never a guess about
            what the practitioner probably charges. */}
        <div className="rounded-xl bg-eh-cream px-4 py-3" data-testid="consult-disclosure-base-fee">
          <div className="text-xs text-eh-muted">{t("consult.disclosure.baseFeeLabel")}</div>
          <div className="font-semibold mt-0.5">{fmtMoney(quote.baseFeeCents ?? 0, currency)}</div>
        </div>
        <div className="rounded-xl bg-eh-cream px-4 py-3" data-testid="consult-disclosure-range">
          <div className="text-xs text-eh-muted">{t("consult.disclosure.rangeLabel")}</div>
          <div className="font-semibold mt-0.5">{t("consult.disclosure.rangeValue", { min: quote.minBillableMinutes, max: quote.maxBillableMinutes })}</div>
        </div>
      </div>

      {/* THE number: what the card is authorised for, stated plainly ("up to $X"), never
          buried among the per-minute figures. */}
      <div className="rounded-xl border border-sol-gold/40 bg-sol-gold/10 px-4 py-4 text-center" data-testid="consult-disclosure-ceiling">
        <div className="text-xs text-eh-muted uppercase tracking-wide">{t("consult.disclosure.ceilingLabel")}</div>
        <div className="text-2xl font-bold text-eh-ink mt-1 tabular-nums">
          {t("consult.disclosure.ceilingValue", { amount: fmtMoney(quote.ceilingCents, currency) })}
        </div>
        <p className="text-xs text-eh-muted mt-1">{t("consult.disclosure.ceilingNote")}</p>
      </div>

      <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800 flex items-start gap-2" data-testid="consult-disclosure-noshow">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>
          {t("consult.disclosure.noShowFee", { amount: fmtMoney(quote.noShowFeeCents, currency) })}
          {" "}
          {t("consult.disclosure.rescheduleWindow", { hours: quote.rescheduleMinNoticeHours })}
        </span>
      </div>

      <label className="flex items-start gap-2.5 text-sm text-eh-ink cursor-pointer" data-testid="consult-disclosure-consent-label">
        <Checkbox
          checked={accepted}
          onCheckedChange={(v) => setAccepted(v === true)}
          className="mt-0.5"
          data-testid="consult-disclosure-consent-checkbox"
        />
        {t("consult.disclosure.consent")}
      </label>

      <Button
        onClick={() => onAccept()}
        disabled={!accepted}
        className="w-full bg-sol-gold text-eh-ink hover:bg-sol-gold/90"
        data-testid="consult-disclosure-continue"
      >
        {t("consult.disclosure.continue")}
      </Button>
    </div>
  );
}
